// context/SubscriptionContext.tsx
// @refresh reset

import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import type { ReactNode } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "./AuthContext";

// ── Types ─────────────────────────────────────────────────────
export type SubscriptionStatus = "trialing" | "active" | "expired" | "cancelled" | "loading" | "none";

export interface Plan {
  id: string;           // "starter" | "growth" | custom uuid
  name: string;
  price: number;        // KSh per month
  price_daily?: number | null;   // starter only — KSh per day
  price_weekly?: number | null;  // starter only — KSh per week
  max_agents: number | null;
  max_shops: number | null;
  features: Record<string, boolean>;
  sort_order: number;
  is_custom?: boolean;
  owner_id?: string | null;
}

export interface FeatureOverrides {
  max_agents?: number | null;
  max_shops?: number | null;
  unlocked_agents?: string[];
  unlocked_shops?: string[];
  [feature: string]: boolean | number | string[] | null | undefined;
}

export interface Subscription {
  id: string;
  owner_id: string;
  status: SubscriptionStatus;
  plan: string;
  price: number;
  trial_started_at: string;
  trial_ends_at: string;
  current_period_start: string | null;
  current_period_end: string | null;
  last_payment_at: string | null;
  last_payment_ref: string | null;
  last_payment_phone: string | null;
  feature_overrides?: FeatureOverrides;
}

interface SubscriptionContextValue {
  subscription: Subscription | null;
  status: SubscriptionStatus;
  daysLeft: number;
  trialTimeLeft: string | null;
  isLocked: boolean;
  isNearExpiry: boolean;
  isDailyPlan: boolean;
  isWeeklyPlan: boolean;
  loading: boolean;

  // Plans
  plans: Plan[];
  currentPlan: Plan | null;
  canAccess: (feature: string) => boolean;

  // Payment flow
  paymentPending: boolean;
  paymentError: string;
  paymentSuccess: boolean;
  pendingAmount: number;
  triggerStkPush: (phone: string, planId?: string, frequency?: "daily" | "weekly" | "monthly" | "yearly") => Promise<void>;
  resetPaymentState: () => void;
  cancelPayment: () => void;

  // Dev helpers
  simulateExpiry: () => void;
  resetSimulation: () => void;

  refetch: () => Promise<void>;
}

// ── Context ───────────────────────────────────────────────────
const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error("useSubscription must be used within SubscriptionProvider");
  return ctx;
}

// ── Provider ──────────────────────────────────────────────────
export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { session, profile } = useAuth();
  const user = session?.user ?? null;
  const isAdmin = profile?.role === "admin";

  const [subscription,       setSubscription]       = useState<Subscription | null>(null);
  const [plans,              setPlans]              = useState<Plan[]>([]);
  const [loading,            setLoading]            = useState(true);
  const [paymentPending,     setPaymentPending]     = useState(false);
  const [paymentError,       setPaymentError]       = useState("");
  const [paymentSuccess,     setPaymentSuccess]     = useState(false);
  const [pendingAmount,      setPendingAmount]      = useState(0);
  const [simulatedExpiry,    setSimulatedExpiry]    = useState(false);
  // Track the current checkout ID so stale callbacks from old attempts are ignored
  const [pendingCheckoutId,  setPendingCheckoutId]  = useState<string | null>(null);
  const pendingCheckoutIdRef = useRef<string | null>(null);
  // Tracks which plan we last reconciled so we don't re-run on every render
  const reconciledPlanRef = useRef<string | null>(null);

  const simulateExpiry  = () => setSimulatedExpiry(true);
  const resetSimulation = () => setSimulatedExpiry(false);

  // ── Fetch plans — standard plans + this owner's custom plan ──
  useEffect(() => {
    if (!user) return;
    supabase
      .from("plans")
      .select("*")
      .order("sort_order")
      .then(({ data, error }) => {
        if (error) console.error("[SubscriptionContext] plans fetch error:", error);
        if (data && data.length > 0) {
          const visible = (data as Plan[]).filter(p => !p.is_custom || p.owner_id === user.id);
          setPlans(visible);
        }
      });
  }, [user?.id]);

  // ── Fetch subscription ────────────────────────────────────────
  const fetchSubscription = useCallback(async () => {
    if (!user || isAdmin) { setSubscription(null); setLoading(false); return; }

    const { data, error } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("owner_id", user.id)
      .single();

    if (error || !data) {
      if (error?.code === "PGRST116") {
        // New owner — create trial row
        await supabase.from("subscriptions").insert({
          owner_id:         user.id,
          status:           "trialing",
          trial_started_at: new Date().toISOString(),
          trial_ends_at:    new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString(),
        });
        return fetchSubscription();
      }
      setSubscription(null);
    } else {
      setSubscription(data as Subscription);
    }
    setLoading(false);
  }, [user, isAdmin]);

  useEffect(() => { fetchSubscription(); }, [fetchSubscription]);

  // ── Realtime: subscription row changes ───────────────────────
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`subscription-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "subscriptions", filter: `owner_id=eq.${user.id}` },
        (payload) => {
          const newSub = payload.new as Subscription;
          setSubscription(newSub);
          if (newSub.status === "active") {
            setPaymentPending(false);
            setPaymentSuccess(true);
            // Enforce / relax agent + shop limits for the newly activated plan
            if (newSub.plan) reconcileForPlan(newSub.plan);
          }
        })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  // ── Realtime + polling: payment result ───────────────────────
  const paymentPendingRef  = useRef(false);
  const paymentStartRef    = useRef<number>(0); // epoch ms when current payment was initiated
  useEffect(() => { paymentPendingRef.current = paymentPending; }, [paymentPending]);
  useEffect(() => { pendingCheckoutIdRef.current = pendingCheckoutId; }, [pendingCheckoutId]);

  useEffect(() => {
    if (!user) return;

    const FAILURE_MESSAGES: Record<string, string> = {
      cancelled:          "You cancelled the M-Pesa prompt. Tap Pay again whenever you're ready.",
      insufficient_funds: "Insufficient M-Pesa balance. Top up and try again.",
      timeout:            "The M-Pesa prompt timed out. Please try again.",
      wrong_pin:          "Wrong M-Pesa PIN entered. Please try again.",
      expired:            "The payment request expired. Please try again.",
      failed:             "Payment was not completed. Please try again.",
    };

    // Realtime: require exact checkout ID match — no time-window fallback.
    // The edge function stores the ID before Safaricom can ever callback, so this is always safe.
    const handleRealtimeFailure = (row: any) => {
      if (!paymentPendingRef.current) return;
      const storedId = pendingCheckoutIdRef.current;
      if (!storedId || row.checkout_request_id !== storedId) return;
      setPaymentError(FAILURE_MESSAGES[row.failure_reason] || "Payment was not completed. Please try again.");
      setPaymentPending(false);
    };

    const handleRealtimeSuccess = (row: any) => {
      if (!paymentPendingRef.current) return;
      const storedId = pendingCheckoutIdRef.current;
      if (!storedId || row.checkout_request_id !== storedId) return;
      setPaymentSuccess(true);
      setPaymentPending(false);
      setTimeout(() => fetchSubscription(), 2000);
    };

    const handlePollRow = (row: any) => {
      const storedId = pendingCheckoutIdRef.current;
      // Only act if we have a checkout ID and it matches exactly — no time-window fallback.
      // The edge function always responds with the ID before Safaricom can send any callback,
      // so there is no legitimate case where storedId is null when a result row exists.
      if (!storedId || row.checkout_request_id !== storedId) return;
      if (row.status === "failed") {
        setPaymentError(FAILURE_MESSAGES[row.failure_reason] || "Payment was not completed. Please try again.");
        setPaymentPending(false);
      } else if (row.status === "completed") {
        setPaymentSuccess(true);
        setPaymentPending(false);
        setTimeout(() => fetchSubscription(), 2000);
      }
    };

    // Realtime — fires as soon as Safaricom callback updates the row
    const channel = supabase
      .channel(`pending-payments-${user.id}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "pending_payments", filter: `owner_id=eq.${user.id}` },
        (payload) => {
          const row = payload.new as any;
          if (row.status === "failed")    handleRealtimeFailure(row);
          if (row.status === "completed") handleRealtimeSuccess(row);
        })
      .subscribe();

    const pollInterval = setInterval(async () => {
      if (!paymentPendingRef.current) return;
      const storedId = pendingCheckoutIdRef.current;
      if (!storedId) return; // Don't poll until we have the checkout ID

      const { data: rows } = await supabase
        .from("pending_payments")
        .select("status, failure_reason, checkout_request_id, created_at")
        .eq("owner_id", user.id)
        .eq("checkout_request_id", storedId)
        .in("status", ["failed", "completed"])
        .limit(1);

      (rows ?? []).forEach(row => handlePollRow(row));
    }, 4000);

    return () => { supabase.removeChannel(channel); clearInterval(pollInterval); };
  }, [user?.id]);

  // ── Derived values ────────────────────────────────────────────
  const now = new Date();

  const computedStatus: SubscriptionStatus = (() => {
    if (loading || !subscription) return "loading";
    if (subscription.status === "trialing") {
      return new Date(subscription.trial_ends_at) < now ? "expired" : "trialing";
    }
    if (subscription.status === "active") {
      if (subscription.current_period_end && new Date(subscription.current_period_end) < now) return "expired";
      return "active";
    }
    return subscription.status;
  })();

  const daysLeft = (() => {
    if (!subscription) return 0;
    const end = computedStatus === "trialing" ? subscription.trial_ends_at : subscription.current_period_end;
    if (!end) return 0;
    return Math.max(0, Math.ceil((new Date(end).getTime() - now.getTime()) / 86400000));
  })();

  const trialTimeLeft = (() => {
    if (!subscription || computedStatus !== "trialing") return null;
    const ms = new Date(subscription.trial_ends_at).getTime() - Date.now();
    if (ms <= 0) return "Expired";
    const mins = Math.ceil(ms / 60000);
    if (mins < 60) return `${mins} minute${mins !== 1 ? "s" : ""}`;
    const hrs = Math.ceil(ms / 3600000);
    if (hrs < 24) return `${hrs} hour${hrs !== 1 ? "s" : ""}`;
    return `${Math.ceil(ms / 86400000)} days`;
  })();

  const isLocked = (simulatedExpiry && computedStatus !== "active") || computedStatus === "expired" || computedStatus === "cancelled";

  // ── Detect plan frequency from period span ────────────────────
  const periodSpanMs = (
    computedStatus === "active" &&
    subscription?.current_period_start &&
    subscription?.current_period_end
  )
    ? new Date(subscription.current_period_end).getTime() - new Date(subscription.current_period_start).getTime()
    : null;

  // Daily  : span ≤ 36 h  (1.5× tolerance for clock drift)
  // Weekly : span ≤ 9 days (1.25× tolerance)
  // Monthly: anything longer
  const isDailyPlan  = !!periodSpanMs && periodSpanMs <= 1.5  * 86_400_000;
  const isWeeklyPlan = !!periodSpanMs && periodSpanMs >  1.5  * 86_400_000 && periodSpanMs <= 9 * 86_400_000;

  // Near-expiry thresholds — when the banner first appears:
  //  • Daily   → last 8 hours  (owner needs time to notice + pay before work starts)
  //  • Weekly  → last 2 days
  //  • Monthly / trial → last 4 days
  const nearExpiryThresholdMs = isDailyPlan
    ? 8  * 60 * 60 * 1000          // 8 hours
    : isWeeklyPlan
    ? 2  * 24 * 60 * 60 * 1000     // 2 days
    : 4  * 24 * 60 * 60 * 1000;    // 4 days (monthly + trial)

  const isNearExpiry = !isLocked && (
    (computedStatus === "trialing" && !!subscription?.trial_ends_at &&
      (new Date(subscription.trial_ends_at).getTime() - Date.now()) <= nearExpiryThresholdMs) ||
    (computedStatus === "active" && !!subscription?.current_period_end &&
      (new Date(subscription.current_period_end).getTime() - Date.now()) <= nearExpiryThresholdMs)
  );

  // ── Plan helpers ──────────────────────────────────────────────
  const currentPlan = plans.find(p => p.id === subscription?.plan) ?? null;

  const canAccess = (feature: string): boolean => {
    // During trial: full access to everything
    if (computedStatus === "trialing") return true;
    // Expired/cancelled: blocked by SubscriptionGuard
    if (isLocked) return false;
    // Active — check overrides first, then plan features
    if (computedStatus === "active" && subscription?.plan) {
      const overrides: FeatureOverrides = subscription.feature_overrides ?? {};
      // Per-owner feature override (boolean) takes full precedence over plan
      if (typeof overrides[feature] === "boolean") return overrides[feature] as boolean;
      const plan = plans.find(p => p.id === subscription.plan);
      if (!plan) return true; // unknown plan — default allow
      // Custom plans are fully unlocked — every feature is included.
      // The only thing that varies on a custom plan is max_agents / max_shops.
      if (plan.is_custom) return true;
      const OPT_IN = ["export", "daily_reports", "scan_to_sell"];
      if (OPT_IN.includes(feature)) return plan.features[feature] === true;
      return plan.features[feature] !== false;
    }
    return true;
  };

  // ── Plan-limit enforcement (agents + shops) ──────────────────
  // Delegates to /api/reconcile-plan so the service key never
  // touches the browser bundle.
  const reconcileForPlan = useCallback(async (planId: string) => {
    if (!user) return;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    try {
      await fetch("/api/reconcile-plan", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ planId }),
      });
    } catch (err) {
      console.error("[reconcileForPlan] failed:", err);
    }
  }, [user]);

  // ── Enforce limits on load ────────────────────────────────────
  // Runs once when both subscription and plans are available.
  // Catches cases where an owner was already over-limit before the
  // feature existed (no new payment needed to trigger enforcement).
  useEffect(() => {
    if (
      subscription?.status === "active" &&
      subscription.plan &&
      plans.length > 0 &&
      reconciledPlanRef.current !== subscription.plan  // only run once per plan
    ) {
      reconciledPlanRef.current = subscription.plan;
      reconcileForPlan(subscription.plan);
    }
  }, [subscription?.status, subscription?.plan, plans.length, reconcileForPlan]);

  // ── STK Push ─────────────────────────────────────────────────
  const triggerStkPush = async (phone: string, planId?: string, frequency: "daily" | "weekly" | "monthly" | "yearly" = "monthly") => {
    setPaymentError("");
    setPaymentPending(true);
    setPaymentSuccess(false);
    setPendingCheckoutId(null);
    pendingCheckoutIdRef.current  = null;
    paymentStartRef.current       = Date.now();

    // Set display amount from local plan data (for UI only — server recomputes from DB)
    // Set display amount from local plan data (for UI only — server recomputes from DB)
    const plan = planId ? plans.find(p => p.id === planId) : null;
    let displayAmount = 0;
    if (plan) {
      if (frequency === "yearly") {
        displayAmount = Math.round(plan.price * 11 / 10) * 10;
      } else if (frequency === "daily" && (plan as any).price_daily != null) {
        displayAmount = (plan as any).price_daily;
      } else if (frequency === "weekly" && (plan as any).price_weekly != null) {
        displayAmount = (plan as any).price_weekly;
      } else {
        displayAmount = plan.price;
      }
    }
    setPendingAmount(displayAmount);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setPaymentError("Your session has expired. Please refresh the page and sign in again.");
        setPaymentPending(false);
        return;
      }

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mpesa-stk-push`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ phone, plan: planId, frequency }),
        }
      );

      let data: any = {};
      const rawText = await res.text();
      try { data = rawText ? JSON.parse(rawText) : {}; } catch {
        setPaymentError(`Server error (${res.status}): ${rawText || "Empty response."}`);
        setPaymentPending(false);
        return;
      }

      if (!res.ok || !data.success) {
        setPaymentError(data.error || data.message || `HTTP ${res.status}: Payment request failed.`);
        setPaymentPending(false);
        return;
      }

      // Store the checkout ID — only callbacks for THIS request will be handled
      if (data.checkoutRequestId) {
        setPendingCheckoutId(data.checkoutRequestId);
        pendingCheckoutIdRef.current = data.checkoutRequestId;
      }

      // 90-second timeout (M-Pesa STK expires after ~60s — give a buffer)
      setTimeout(() => {
        setPaymentPending(prev => {
          if (prev) setPaymentError("M-Pesa request timed out. If you received a prompt, wait for it to complete. Otherwise, try again.");
          return false;
        });
      }, 90_000);

    } catch (err: any) {
      setPaymentError(err.message || "Something went wrong. Please try again.");
      setPaymentPending(false);
    }
  };

  const resetPaymentState = () => {
    setPaymentPending(false);
    setPaymentError("");
    setPaymentSuccess(false);
    setPendingCheckoutId(null);
    pendingCheckoutIdRef.current = null;
  };

  // Called by "Stop waiting" button — clears spinner with a neutral message
  const cancelPayment = () => {
    if (!paymentPendingRef.current) return;
    setPaymentPending(false);
    setPaymentError("Payment wait cancelled. If you approved it on your phone, your subscription will activate automatically within a minute.");
    setPendingCheckoutId(null);
    pendingCheckoutIdRef.current = null;
  };

  return (
    <SubscriptionContext.Provider value={{
      subscription, status: computedStatus, daysLeft, trialTimeLeft,
      isLocked, isNearExpiry, isDailyPlan, isWeeklyPlan, loading,
      plans, currentPlan, canAccess,
      paymentPending, paymentError, paymentSuccess, pendingAmount,
      triggerStkPush, resetPaymentState, cancelPayment,
      simulateExpiry, resetSimulation,
      refetch: fetchSubscription,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}
